import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FollowButton } from '@/components/profile/FollowButton';
import { FollowersList } from '@/components/profile/FollowersList';
import { FollowingList } from '@/components/profile/FollowingList';
import { useFollow } from '@/hooks/useFollow';
import { Video, Award } from 'lucide-react';
import { toast } from 'sonner';

const UserProfile = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [isCreator, setIsCreator] = useState(false);
  const [streams, setStreams] = useState<any[]>([]);
  const [recordings, setRecordings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const { fansCount, followingCount } = useFollow(userId || null);

  useEffect(() => {
    if (!userId) return;
    
    // Redirect to own profile if viewing self
    if (currentUser && userId === currentUser.id) {
      navigate('/profile');
      return;
    }

    loadProfile();
    loadStreams();
    loadRecordings();
    checkIfCreator();
  }, [userId, currentUser]);

  const loadProfile = async () => {
    if (!userId) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } else if (data) {
      setProfile(data);
    }
    setLoading(false);
  };

  const checkIfCreator = async () => {
    if (!userId) return;

    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'creator')
      .maybeSingle();

    setIsCreator(!!data);
  };

  const loadStreams = async () => {
    if (!userId) return;

    const { data } = await supabase
      .from('streams')
      .select('*')
      .eq('creator_id', userId)
      .eq('status', 'live')
      .order('started_at', { ascending: false });

    if (data) setStreams(data);
  };

  const loadRecordings = async () => {
    if (!userId) return;

    const { data } = await supabase
      .from('recordings')
      .select('*')
      .eq('creator_id', userId)
      .eq('is_published', true)
      .eq('status', 'completed')
      .order('published_at', { ascending: false })
      .limit(20);

    if (data) setRecordings(data);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <div className="container py-8 max-w-4xl flex-1">
          <Card className="clean-card">
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">Loading profile...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <div className="container py-8 max-w-4xl flex-1">
          <Card className="clean-card">
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">Profile not found</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      <div className="container py-8 max-w-4xl flex-1">
        <BackButton to="/" />
        {/* Profile Header */}
        <Card className="mb-6 clean-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <Avatar className="h-24 w-24 border-2 border-primary/10">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-3xl bg-primary/10 text-primary">
                  {profile.display_name?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                      {profile.display_name}
                      {profile.is_verified && (
                        <Badge variant="secondary" className="rounded-full">✓ Verified</Badge>
                      )}
                      {isCreator && (
                        <Badge variant="default" className="rounded-full">Creator</Badge>
                      )}
                    </h1>
                    <p className="text-sm text-muted-foreground">@{profile.username}</p>
                  </div>
                  {currentUser && userId && (
                    <FollowButton userId={userId} />
                  )}
                </div>
                {profile.bio && (
                  <p className="text-muted-foreground mb-3">{profile.bio}</p>
                )}
                <div className="flex gap-4">
                  <div>
                    <span className="font-bold text-foreground">{fansCount}</span>
                    <span className="text-sm text-muted-foreground ml-1">Fans</span>
                  </div>
                  <div>
                    <span className="font-bold text-foreground">{followingCount}</span>
                    <span className="text-sm text-muted-foreground ml-1">Following</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="streams" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-white border">
            <TabsTrigger value="streams">
              <Video className="h-4 w-4 mr-2" />
              Live
            </TabsTrigger>
            <TabsTrigger value="recordings">
              <Video className="h-4 w-4 mr-2" />
              Recordings
            </TabsTrigger>
            <TabsTrigger value="followers">Fans</TabsTrigger>
            <TabsTrigger value="following">Following</TabsTrigger>
          </TabsList>

          <TabsContent value="streams">
            <Card className="clean-card">
              <CardContent className="p-6">
                {streams.length === 0 ? (
                  <div className="text-center py-8">
                    <Video className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">No active streams</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {streams.map((stream) => (
                      <Card
                        key={stream.id}
                        className="clean-card hover-lift cursor-pointer"
                        onClick={() => navigate(`/stream/${stream.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <h3 className="font-semibold text-foreground">{stream.title}</h3>
                              <p className="text-sm text-muted-foreground">{stream.viewer_count} viewers</p>
                            </div>
                            <Badge variant="destructive" className="animate-pulse">LIVE</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recordings">
            <Card className="clean-card">
              <CardContent className="p-6">
                {recordings.length === 0 ? (
                  <div className="text-center py-8">
                    <Video className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">No recordings available</p>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {recordings.map((recording) => (
                      <Card
                        key={recording.id}
                        className="clean-card hover-lift cursor-pointer group"
                        onClick={() => navigate(`/watch/${recording.id}`)}
                      >
                        <div className="relative aspect-video overflow-hidden rounded-t-lg">
                          {recording.thumbnail_url ? (
                            <img
                              src={recording.thumbnail_url}
                              alt={recording.title}
                              className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full bg-muted">
                              <Video className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded">
                            {Math.floor(recording.duration / 60)}:{(recording.duration % 60).toString().padStart(2, '0')}
                          </div>
                        </div>
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-sm text-foreground line-clamp-2 mb-1">
                            {recording.title}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {recording.view_count || 0} views
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="followers">
            {userId && <FollowersList userId={userId} />}
          </TabsContent>

          <TabsContent value="following">
            {userId && <FollowingList userId={userId} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserProfile;
